import React from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import { appColors } from '../../Config/Theme';
import { HeadcountCounts, ViewAction, getViewActionLabel, getCardSummaryText } from '../../Utils/rosterViewHelpers';

interface RosterGroupCardProps {
  label: string;
  counts: HeadcountCounts;
  onClick: () => void;
  isClosed?: boolean;
}

const RosterGroupCard: React.FC<RosterGroupCardProps> = ({ label, counts, onClick, isClosed = false }) => {
  const action = isClosed ? 'View' as ViewAction : getViewActionLabel(counts);
  const summaryText = getCardSummaryText(counts);
  const isReview = action === 'Review';

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: appColors.surface.card,
        border: isReview
          ? `2px solid ${appColors.status.error}`
          : `1px solid ${appColors.border.default}`,
        transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s',
        '&:hover': {
          borderColor: isReview ? appColors.status.errorDark : appColors.primary.main,
          boxShadow: appColors.shadow.cardHover,
          transform: 'translateY(-1px)',
        },
        '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
      }}
      aria-label={`${action} ${label}, ${counts.total} ${counts.total === 1 ? 'person' : 'people'}, ${summaryText}`}
    >
      {/* Card body */}
      <Box sx={{ px: 2, py: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: appColors.text.heading, lineHeight: 1.3, mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block' }}>
          {counts.total} {counts.total === 1 ? 'person' : 'people'} · {summaryText}
        </Typography>
        {/* Quiet text CTA */}
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            fontWeight: 700,
            fontSize: '0.78rem',
            color: isReview ? appColors.status.error : appColors.primary.main,
          }}
        >
          {action} →
        </Typography>
      </Box>
    </ButtonBase>
  );
};

export default RosterGroupCard;
